import { ErrorHandler, LOCALE_ID, NgModule, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule, Title } from "@angular/platform-browser";
import { NgIdleKeepaliveModule } from "@ng-idle/keepalive";
import { TranslateModule } from "@ngx-translate/core";
import { MessageService } from "@progress/kendo-angular-l10n";
import { JumpstartComponentsModule as JumpstartComponentsModule_v3 } from "@wk/components-v3-angular17";

import { ChartistModule } from "ng-chartist";
import { NgxPaginationModule } from "ngx-pagination";

import {
    AnalyticsService,
    AppSettingsService,
    ApplicationsService,
    AuthInterceptor,
    AuthService,
    BroadcastService,
    CardService,
    CardViewService,
    CategoriesService,
    ColumnDataLayoutService,
    CountersService,
    FiltersService,
    HelpersService,
    HttpHandlerInterceptor,
    IFirmContactInformationService,
    IFirmContactsService,
    IFirmDeliveryMethodsService,
    IFirmGroupsService,
    IFirmRolesService,
    IFirmUserRolesService,
    IFirmUsersService,
    IfirmTaxCardsService,
    IfirmTaxDocumentService,
    IfirmTaxReturnStatusesService,
    CryptoService,
    KendoMessageService,
    MappingOfStatusesService,
    PrinterCardsService,
    ResponseErrorHandlerService,
    SentryErrorHandler,
    StepsService,
    TaxReturnStatusesService,
    UserDashboardSettingsService,
    UsersService,
    UtilsService,
    DashboardSelectComponent,
} from "@shared/index";

import { MicroServiceError } from "./shared/microservice-error/microservice-error";

import {
    SettingMappingColumnComponent,
    SettingMappingStatusComponent,
    SettingsFilesCategoryComponent,
    SettingsGeneralComponent,
    SettingsMappingComponent,
    SettingsUserEditManagerComponent,
    SettingsMainComponent,
    SettingsRenameDashboardComponent,
    DashboardEditComponent,
    SettingMappingModelService,
} from "@app/settings-components";
import { AuditTrailComponent } from "@app/settings/audit-trail/audit-trail.component";
import { ActiveViewComponent, NotStartedComponent } from "@app/view-components";
import { AppComponent } from "@app/app.component";
import { CardJobsBillingComponent, CardT1Component } from "@app/card-components";
import { NavBarComponent, DisplayComponent } from "@app/navbar-component";
import { NotifyPopupComponent } from "@app/notify-popup-component";
import { SearchComponent, SearchResultComponent } from "@app/search-component";
import { KendoComboBoxWrapper } from "@app/shared/components/kendo-combobox-wrapper";

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from "@angular/common/http";
import { Routing } from "./app.routing";

import { ColumnComponent } from "@app/column-component";
import { HeaderComponent } from "./header-component/header.component";
import { DashboardManagementService } from "./shared/dashboard-management/dashboard-management.service";
import { DashboardsService } from "./shared/dashboards-service/dashboards.service";
import { IframeMessagesService } from "./shared/iframe-messages-service/iframe-messages.service";

import { DatePipe, registerLocaleData } from "@angular/common";
import localeFrCA from "@angular/common/locales/fr-CA";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ButtonsModule } from "@progress/kendo-angular-buttons";
import { DateInputsModule } from "@progress/kendo-angular-dateinputs";
import { DialogsModule, WindowModule } from "@progress/kendo-angular-dialog";
import { DropDownsModule } from "@progress/kendo-angular-dropdowns";
import { EditorModule } from "@progress/kendo-angular-editor";
import { GridModule } from "@progress/kendo-angular-grid";
import { InputsModule } from "@progress/kendo-angular-inputs";
import { IntlModule, load } from "@progress/kendo-angular-intl";
import { RippleModule } from "@progress/kendo-angular-ripple";
import { PopoverModule as KendoPopoverModule, TooltipModule as KendoTooltipModule } from "@progress/kendo-angular-tooltip";
import calendar from "cldr-data/main/fr/ca-gregorian.json";
import dateFields from "cldr-data/main/fr/dateFields.json";
import numbers from "cldr-data/main/fr/numbers.json";
import timeZoneNames from "cldr-data/main/fr/timeZoneNames.json";
import likelySubtags from "cldr-data/supplemental/likelySubtags.json";
import weekData from "cldr-data/supplemental/weekData.json";
import { BsModalService, ModalModule } from "ngx-bootstrap/modal";
import { PopoverModule as BootstrapPopoverModule } from "ngx-bootstrap/popover";
import { TabsModule } from "ngx-bootstrap/tabs";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { AccessDeniedComponent } from "./access-denied/access-denied.component";
import { AccessUnauthorizedComponent } from "./access-unauthorized/access-unauthorized.component";
import { AssigneesColumnsWrapperComponent } from "./assignees-columns-wrapper/assignees-columns-wrapper.component";
import { JobsAssigneesColumnsWrapperComponent } from "./assignees-columns-wrapper/jobs-assignees-columns-wrapper.component";
import { BulkChangeComponent } from "./bulk-change/bulk-change.component";
import { UserInitialPipe } from "./card-components/card-t1-component/UserInitialPipe/user-initial.pipe";
import { UserNamePipe } from "./card-components/card-t1-component/UserNamePipe/user-name.pipe";
import { CardModalComponent } from "./card-modal-component/card-modal-component";
import { WestPanelComponent, SouthPanelComponent, NorthPanelComponent, CardPanelComponent } from "./card-modal-component/panels";
import { DevToolsComponent } from "./dev-tools/dev-tools.component";
import { FilterMenuComponent } from "./filter-menu/filter-menu.component";
import {
    JobsFilterMenuComponent,
    TargetStartDatesComponent,
    TargetEndDatesComponent,
    PeriodEndDatesComponent,
    DueOnDatesComponent,
} from "@app/jobs-filter-menu";
import { PendingChangesPopupComponent } from "./shared/pending-changes-popup/pending-changes-popup.component";
import { RealTimeNotificationsModule } from "./real-time-notifications/real-time-notifications.module";
import { CancelConfirmationComponent } from "./settings-components/settings-dashboard-management-component/cancel-confirmation/cancel-confirmation.component";
import { DashboardAccessComponent } from "./settings-components/settings-dashboard-management-component/dashboard-access/dashboard-access.component";
import { SettingsDashboardManagementComponent } from "./settings-components/settings-dashboard-management-component/settings-dashboard-management.component";
import { FirmsService } from "./shared/firms-service/firms.service";
import { SharedModule } from "./shared/shared.module";
import { SortMenuComponent } from "@app/sort-menu";
import { UnauthorizedRouteComponent } from "./unauthorized-route/unauthorized-route.component";
import { AllViewComponent } from "./view-components/all-view-component/all-view.component";
import { AllViewContainerComponent } from "@app/view-components/all-view-container";
import { AssigneesViewComponent } from "./view-components/assignees-view/assignees-view.component";
import { CompletedViewComponent } from "./view-components/completed-view-component/completed-view.component";
import { PaginationComponent } from "./view-components/pagination-component/pagination-component";
import { ViewComponent } from "./view-components/view-component/view.component";
import { CardDisplayService } from "./shared/card-display/card-display.service";

import { DragComponent, DropListComponent } from "./drag-and-drop";
import { CdkScrollable, ScrollingModule } from "@angular/cdk/scrolling";
import { A11yModule } from "@angular/cdk/a11y";
import { DragAndDropService } from "./drag-and-drop/drag-and-drop.service";
import { DragPlaceholderDirective } from "./drag-and-drop/drag-placeholder.directive";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";

registerLocaleData(localeFrCA, "fr-CA");

load(likelySubtags, weekData, numbers, calendar, dateFields, timeZoneNames);

async function appInit(): Promise<void> {
    const config = inject(AppSettingsService);
    const auth = inject(AuthService);
    const iframe = inject(IframeMessagesService);
    const analytics = inject(AnalyticsService);
    const errorHandler = inject(ErrorHandler);
    console.time("Application responsive elapsed time");
    span_AppResponsiveTimestamp = new Date();

    return config.getLoadSettings().then(() => {
        iframe.init();
        auth.init();
        analytics.init();
        if (errorHandler instanceof SentryErrorHandler) {
            (errorHandler as SentryErrorHandler).initSentry();
        }
    });
}
export let span_AppResponsiveTimestamp = null;
export function provideErrorHandler() {
    if (isDevMode()) {
        return new ErrorHandler();
    } else {
        return new SentryErrorHandler();
    }
}

@NgModule({
    bootstrap: [AppComponent],
    declarations: [
        AccessDeniedComponent,
        ActiveViewComponent,
        AppComponent,
        AssigneesColumnsWrapperComponent,
        JobsAssigneesColumnsWrapperComponent,
        AssigneesViewComponent,
        BulkChangeComponent,
        CardJobsBillingComponent,
        CardModalComponent,
        CardT1Component,
        ColumnComponent,
        CompletedViewComponent,
        DevToolsComponent,
        FilterMenuComponent,
        JobsFilterMenuComponent,
        PendingChangesPopupComponent,
        SortMenuComponent,
        HeaderComponent,
        NavBarComponent,
        NotStartedComponent,
        NotifyPopupComponent,
        SearchComponent,
        SearchResultComponent,
        SettingMappingColumnComponent,
        AuditTrailComponent,
        SettingMappingStatusComponent,
        SettingsDashboardManagementComponent,
        SettingsFilesCategoryComponent,
        SettingsGeneralComponent,
        SettingsMainComponent,
        DashboardSelectComponent,
        SettingsRenameDashboardComponent,
        DashboardEditComponent,
        SettingsMappingComponent,
        SettingsUserEditManagerComponent,
        UnauthorizedRouteComponent,
        ViewComponent,
        AllViewComponent,
        AllViewContainerComponent,
        PaginationComponent,
        DashboardAccessComponent,
        CancelConfirmationComponent,
        AccessUnauthorizedComponent,
        UserNamePipe,
        UserInitialPipe,
        KendoComboBoxWrapper,
    ],
    imports: [
        BrowserAnimationsModule,
        BrowserModule,
        ButtonsModule,
        ChartistModule,
        DateInputsModule,
        DialogsModule,
        DropDownsModule,
        EditorModule,
        FormsModule,
        IntlModule,
        KendoTooltipModule,
        ModalModule,
        NgIdleKeepaliveModule.forRoot(),
        JumpstartComponentsModule_v3,
        NgxPaginationModule,
        ReactiveFormsModule,
        RealTimeNotificationsModule,
        RippleModule,
        Routing,
        SharedModule,
        TabsModule,
        TooltipModule,
        TranslateModule.forRoot({
            loader: provideTranslateHttpLoader({ prefix: "assets/i18n/", suffix: ".json" }),
        }),
        ScrollingModule,
        GridModule,
        KendoPopoverModule,
        BootstrapPopoverModule,
        InputsModule,
        WindowModule,
        WestPanelComponent,
        SouthPanelComponent,
        NorthPanelComponent,
        CardPanelComponent,
        DragComponent,
        DragPlaceholderDirective,
        DropListComponent,
        CdkScrollable,
        A11yModule,
        DisplayComponent,
        TargetStartDatesComponent,
        TargetEndDatesComponent,
        PeriodEndDatesComponent,
        DueOnDatesComponent,
    ],
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideAppInitializer(appInit),
        { provide: ErrorHandler, useFactory: provideErrorHandler },
        { provide: LOCALE_ID, useValue: localStorage.getItem("lang") ? localStorage.getItem("lang") : "en-CA" },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpHandlerInterceptor, multi: true },
        { provide: MessageService, useClass: KendoMessageService },
        AppComponent,
        AnalyticsService,
        ApplicationsService,
        AppSettingsService,
        AuthService,
        BroadcastService,
        BsModalService,
        CardService,
        CardDisplayService,
        CardViewService,
        CategoriesService,
        ColumnDataLayoutService,
        CountersService,
        DashboardManagementService,
        DashboardsService,
        DragAndDropService,
        FiltersService,
        FirmsService,
        HelpersService,
        IFirmContactInformationService,
        IFirmContactsService,
        IFirmDeliveryMethodsService,
        IFirmGroupsService,
        IFirmRolesService,
        IFirmUserRolesService,
        IfirmTaxCardsService,
        IfirmTaxDocumentService,
        IfirmTaxReturnStatusesService,
        IFirmUsersService,
        IframeMessagesService,
        CryptoService,
        MappingOfStatusesService,
        PrinterCardsService,
        ResponseErrorHandlerService,
        MicroServiceError,
        SettingMappingModelService,
        StepsService,
        TaxReturnStatusesService,
        UserDashboardSettingsService,
        UsersService,
        UtilsService,
        DatePipe,
        Title,
        DisplayComponent,
        provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    ],
})
export class AppModule {}
